// Phase-1 Travel Partners enterprise dashboard.
// Master metrics + 12-module tabs + dynamic supplier table.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api, formatApiErrorDetail } from '../../lib/api';
import SupplierForm from './SupplierForm';
import { StatCard, StatusBadge, HealthBadge, EmptyState } from './partnersPrimitives';
import { SparklineCard } from './Sparkline';

const MODULES = [
  { key: 'all',       label: 'All Modules',     emoji: '🌐' },
  { key: 'flights',   label: 'Flights',         emoji: '✈️' },
  { key: 'hotels',    label: 'Hotels',          emoji: '🏨' },
  { key: 'trains',    label: 'Trains',          emoji: '🚄' },
  { key: 'buses',     label: 'Buses',           emoji: '🚌' },
  { key: 'cabs',      label: 'Cabs',            emoji: '🚕' },
  { key: 'villas',    label: 'Villas',          emoji: '🏡' },
  { key: 'packages',  label: 'Packages',        emoji: '🎒' },
  { key: 'tours',     label: 'Tours',           emoji: '🗺️' },
  { key: 'visa',      label: 'Visa',            emoji: '📋' },
  { key: 'cruise',    label: 'Cruise',          emoji: '🚢' },
  { key: 'forex',     label: 'Forex',           emoji: '💱' },
  { key: 'insurance', label: 'Insurance',       emoji: '🛡️' },
];

const PAGE_SIZE = 20;

const AdminPartners = () => {
  const [stats, setStats] = useState(null);
  const [activeModule, setActiveModule] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ count: 0, partners: [] });
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get('/admin/partners/stats');
      setStats(r.data);
    } catch (e) {
      toast.error('Failed to load dashboard stats');
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const r = await api.get('/admin/comparison/analytics/summary', { params: { window_hours: 24, buckets: 24 } });
      setAnalytics(r.data);
    } catch {
      // Non-fatal — sparklines just hide
    }
  }, []);

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (activeModule !== 'all') params.module = activeModule;
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const r = await api.get('/admin/partners', { params });
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, [page, activeModule, statusFilter, search]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);
  useEffect(() => { loadPartners(); }, [loadPartners]);

  const onAdd = () => { setEditing(null); setFormOpen(true); };
  const onEdit = (p) => { setEditing(p); setFormOpen(true); };
  const onDelete = async (p) => {
    if (!window.confirm(`Delete supplier "${p.display_name || p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/partners/${p.id}`);
      toast.success('Supplier deleted');
      loadPartners(); loadStats();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Delete failed');
    }
  };
  const onActivate = async (p) => {
    try {
      await api.post(`/admin/partners/${p.id}/${p.status === 'Active' ? 'deactivate' : 'activate'}`);
      toast.success(p.status === 'Active' ? 'Supplier suspended' : 'Supplier activated');
      loadPartners(); loadStats();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Status change failed');
    }
  };
  const onTest = async (p) => {
    toast.message(`Testing ${p.display_name || p.name}…`);
    try {
      const r = await api.post(`/admin/partners/${p.id}/test`, { method: 'GET' });
      if (r.data.ok) toast.success(`${r.data.state.toUpperCase()} · ${r.data.latency_ms}ms · HTTP ${r.data.http_status}`);
      else toast.error(`${r.data.state} · ${r.data.error || 'unknown error'}`);
      loadStats();
      loadPartners();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Test failed');
    }
  };

  const totalPages = Math.max(1, Math.ceil((data.count || 0) / PAGE_SIZE));
  const lastSync = useMemo(() => stats?.last_sync ? new Date(stats.last_sync).toLocaleString('en-IN') : '—', [stats]);

  // ─── Phase-2 Comparison Engine sparklines ───────────────────────────
  const spark = useMemo(() => {
    const buckets = analytics?.buckets || [];
    const fmt = (iso) => new Date(iso).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' });
    const buildTooltip = (b) =>
      `${fmt(b.start)} → ${fmt(b.end)}\nruns: ${b.runs} · offers: ${b.total_offers}\ndedupe: ${(b.dedupe_rate * 100).toFixed(1)}%\ntop: ${b.top_supplier || '—'}`;
    return {
      dedupe: buckets.map((b) => ({ y: b.dedupe_rate, tooltip: buildTooltip(b) })),
      wins:   buckets.map((b) => ({ y: Object.values(b.supplier_wins || {}).reduce((a, c) => a + c, 0), tooltip: buildTooltip(b) })),
      conf:   buckets.map((b) => ({ y: b.avg_merge_confidence, tooltip: buildTooltip(b) })),
    };
  }, [analytics]);

  const totals = analytics?.totals;
  // Compare last quarter vs prior quarter for delta arrows.
  // Returns null when there's not enough baseline traffic to make the delta meaningful —
  // SparklineCard will hide the chip in that case.
  const computeDelta = (arr) => {
    if (!arr || arr.length < 4) return null;
    const cut = Math.max(1, Math.floor(arr.length / 4));
    const recent = arr.slice(-cut).reduce((a, x) => a + (x.y || 0), 0) / cut;
    const prior  = arr.slice(-cut * 2, -cut).reduce((a, x) => a + (x.y || 0), 0) / cut;
    // Need a real baseline to compute a percentage trend.
    if (prior < 0.001) return null;
    const raw = ((recent - prior) / prior) * 100;
    // Clamp to ±999% — anything larger is noise from sparse data.
    return Math.max(-999, Math.min(999, raw));
  };

  return (
    <div className="space-y-6" data-testid="admin-partners-page">
      {/* Heading */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Travel Partners</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage supplier APIs across all 12 travel categories. Last sync: <span className="font-semibold">{lastSync}</span>
          </p>
        </div>
        <button onClick={onAdd} className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md px-4 py-2 shadow" data-testid="add-supplier-btn">
          + Add Supplier
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total APIs"     value={stats?.total ?? '—'}    accent="gray"  testId="stat-total"    sub="All suppliers" />
        <StatCard label="Active"         value={stats?.active ?? '—'}   accent="green" testId="stat-active"   sub="Serving requests" />
        <StatCard label="Pending"        value={stats?.pending ?? '—'}  accent="amber" testId="stat-pending"  sub="Awaiting credentials" />
        <StatCard label="Inactive"       value={stats?.inactive ?? '—'} accent="gray"  testId="stat-inactive" sub="Draft / Suspended" />
        <StatCard label="Failed APIs"    value={stats?.failed ?? '—'}   accent="red"   testId="stat-failed"   sub="Last test failed" />
      </div>

      {/* Phase-2 Comparison Engine analytics · last 24h */}
      {analytics && (
        <div data-testid="partners-analytics-strip">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wider font-semibold text-gray-500">
              Comparison Engine · last {analytics.window_hours}h
            </p>
            <span className="text-[11px] text-gray-400">
              {totals?.runs ?? 0} runs · top supplier: <span className="font-semibold text-gray-700 dark:text-gray-300">{totals?.top_supplier || '—'}</span>
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SparklineCard
              testId="spark-dedupe-rate"
              label="Dedupe rate"
              value={`${((totals?.dedupe_rate || 0) * 100).toFixed(1)}%`}
              sub={`${totals?.duplicates_merged ?? 0} duplicates merged · ${totals?.total_offers ?? 0} offers fetched`}
              delta={computeDelta(spark.dedupe)}
              data={spark.dedupe}
              color="red"
              footnote="Higher = more supplier overlap consolidated into canonical inventory"
            />
            <SparklineCard
              testId="spark-supplier-wins"
              label="Supplier wins"
              value={`${Object.values(totals?.supplier_wins || {}).reduce((a, c) => a + c, 0)}`}
              sub={`${Object.keys(totals?.supplier_wins || {}).length} unique winners`}
              delta={computeDelta(spark.wins)}
              data={spark.wins}
              color="blue"
              footnote="Total canonical items where each supplier's offer ranked #1"
            />
            <SparklineCard
              testId="spark-merge-confidence"
              label="Avg merge confidence"
              value={`${((totals?.avg_merge_confidence || 0) * 100).toFixed(1)}%`}
              sub="Higher = more deterministic key matches vs. fuzzy"
              delta={computeDelta(spark.conf)}
              data={spark.conf}
              color="green"
              footnote="Reflects fingerprint-match strength across all runs"
            />
          </div>
        </div>
      )}

      {/* Module-wise breakdown */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        {MODULES.filter((m) => m.key !== 'all').map((m) => {
          const s = stats?.by_module?.[m.key] || { total: 0, active: 0 };
          return (
            <button
              key={m.key}
              onClick={() => { setActiveModule(m.key); setPage(1); }}
              data-testid={`module-tab-${m.key}`}
              className={`rounded-lg border px-3 py-2 text-left transition-colors ${activeModule === m.key ? 'border-red-500 bg-red-50 dark:bg-red-950/40' : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-red-200'}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-base">{m.emoji}</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{m.label}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {s.total} suppliers · <span className="text-green-600 font-semibold">{s.active} active</span>
              </p>
            </button>
          );
        })}
      </div>

      {/* Filters bar */}
      <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-4 py-3">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Module:</span>
          <select value={activeModule} onChange={(e) => { setActiveModule(e.target.value); setPage(1); }} className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm" data-testid="filter-module">
            {MODULES.map((m) => <option key={m.key} value={m.key}>{m.emoji} {m.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Status:</span>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm" data-testid="filter-status">
            <option value="">All</option>
            <option>Active</option>
            <option>Pending</option>
            <option>Suspended</option>
            <option>Draft</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search supplier name…"
            data-testid="filter-search"
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm"
          />
        </div>
        <button onClick={() => { loadPartners(); loadStats(); }} className="text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-red-600" data-testid="refresh-btn">↻ Refresh</button>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-950 text-gray-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Supplier</th>
                <th className="text-left px-4 py-3">Modules</th>
                <th className="text-left px-4 py-3">Environment</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Health</th>
                <th className="text-right px-4 py-3">Bookings</th>
                <th className="text-right px-4 py-3">Markup</th>
                <th className="text-right px-4 py-3">Priority</th>
                <th className="text-right px-4 py-3 w-[260px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="text-center py-10 text-gray-500">Loading suppliers…</td></tr>
              )}
              {!loading && (data.partners?.length || 0) === 0 && (
                <tr><td colSpan={9} className="py-2">
                  <EmptyState
                    title="No suppliers in this view"
                    sub="Try a different module/status or click + Add Supplier to onboard a new partner."
                    action={<button onClick={onAdd} className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md px-4 py-2">+ Add Supplier</button>}
                  />
                </td></tr>
              )}
              {!loading && data.partners?.map((p) => (
                <tr key={p.id} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40" data-testid={`supplier-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.logo_url ? (
                        <img src={p.logo_url} alt={p.name} className="h-8 w-8 rounded object-cover bg-gray-100" />
                      ) : (
                        <div className="h-8 w-8 rounded bg-gradient-to-br from-red-500 to-red-700 text-white flex items-center justify-center font-bold text-xs">
                          {(p.display_name || p.name || '?').slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{p.display_name || p.name}</p>
                        <p className="text-xs text-gray-400">{p.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(p.modules || []).map((m) => (
                        <span key={m} className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-0.5 text-xs">{m}</span>
                      ))}
                      {(!p.modules || !p.modules.length) && <span className="text-gray-400 text-xs italic">—</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full text-xs font-semibold px-2 py-0.5 ${p.environment === 'production' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                      {p.environment || 'sandbox'}
                    </span>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-3"><HealthBadge state={p.health?.state} latency={p.health?.latency_ms} /></td>
                  <td className="px-4 py-3 text-right font-semibold">{p.bookings_count || 0}</td>
                  <td className="px-4 py-3 text-right">{p.settings?.markup_percent ?? p.commission_percent ?? 0}%</td>
                  <td className="px-4 py-3 text-right">{p.settings?.priority ?? '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => onTest(p)} className="text-xs font-semibold border border-gray-300 dark:border-gray-700 rounded px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-800" data-testid={`test-${p.id}`}>Test</button>
                      <button onClick={() => onActivate(p)} className={`text-xs font-semibold rounded px-2 py-1 ${p.status === 'Active' ? 'border border-amber-300 text-amber-700 hover:bg-amber-50' : 'border border-green-300 text-green-700 hover:bg-green-50'}`} data-testid={`toggle-${p.id}`}>
                        {p.status === 'Active' ? 'Suspend' : 'Activate'}
                      </button>
                      <button onClick={() => onEdit(p)} className="text-xs font-semibold border border-gray-300 dark:border-gray-700 rounded px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-800" data-testid={`edit-${p.id}`}>Edit</button>
                      <button onClick={() => onDelete(p)} className="text-xs font-semibold border border-red-300 text-red-700 rounded px-2 py-1 hover:bg-red-50" data-testid={`delete-${p.id}`}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data.count > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-800 text-sm text-gray-500">
            <span>Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, data.count)} of {data.count}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 disabled:opacity-40">Prev</button>
              <span className="px-3 py-1">{page} / {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Recent Activity</h3>
        <div className="space-y-1.5">
          {(stats?.recent_activity || []).length === 0 && <p className="text-sm text-gray-500">No activity yet — add or test a supplier to start the audit trail.</p>}
          {(stats?.recent_activity || []).map((a) => (
            <div key={a.id} className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-300">
              <span className="text-gray-400 font-mono">{new Date(a.timestamp).toLocaleString('en-IN')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{a.partner_id}</span>
              <span>·</span>
              <span>{a.action}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Form Modal */}
      {formOpen && (
        <SupplierForm
          initialPartner={editing}
          onClose={() => setFormOpen(false)}
          onSaved={() => { loadPartners(); loadStats(); }}
        />
      )}
    </div>
  );
};

export default AdminPartners;
